import React, { useState, useEffect, useRef } from 'react';
import { Modal } from '../../shared/components/Modal';
import { Button } from '../../shared/components/Button';
import { FormulaBlock } from '../ast/document.types';
import { renderSmartKatex } from '../ast/smartLatex';
import { Sigma, Check } from 'lucide-react';

interface FormulaEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  formulaBlock: FormulaBlock | null;
  sectionId: string | null;
  onSave: (sectionId: string, blockId: string, updates: Partial<FormulaBlock['data']>) => void;
}

const COMMON_SYMBOLS = [
  { label: 'Fraction', latex: '\\frac{a}{b}' },
  { label: 'Sqrt', latex: '\\sqrt{x}' },
  { label: 'Subscript', latex: 'x_1' },
  { label: 'Superscript', latex: 'x^2' },
  { label: 'Sum', latex: '\\sum_{i=1}^{n}' },
  { label: 'Integral', latex: '\\int_{a}^{b} f(x) dx' },
  { label: 'Delta', latex: '\\Delta V' },
  { label: 'Omega (Ω)', latex: '\\Omega' },
  { label: 'Micro (μ)', latex: '\\mu' },
  { label: 'Pi (π)', latex: '\\pi' },
  { label: 'Times', latex: '\\times' },
  { label: 'Approx', latex: '\\approx' },
];

export const FormulaEditModal: React.FC<FormulaEditModalProps> = ({
  isOpen,
  onClose,
  formulaBlock,
  sectionId,
  onSave,
}) => {
  const [latex, setLatex] = useState('');
  const [caption, setCaption] = useState('');
  const [equationNumber, setEquationNumber] = useState('');
  const [displayMode, setDisplayMode] = useState(true);
  const previewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (formulaBlock) {
      setLatex(formulaBlock.data.latex || '');
      setCaption(formulaBlock.data.caption || '');
      setEquationNumber(formulaBlock.data.equationNumber || '');
      setDisplayMode(formulaBlock.data.displayMode !== false);
    }
  }, [formulaBlock]);

  useEffect(() => {
    if (previewRef.current && isOpen) {
      renderSmartKatex(latex || 'x = 0', previewRef.current, { displayMode });
    }
  }, [latex, displayMode, isOpen]);

  if (!formulaBlock || !sectionId) return null;

  const handleInsertSymbol = (symLatex: string) => {
    setLatex((prev) => prev + (prev ? ' ' : '') + symLatex);
  };

  const handleSave = () => {
    onSave(sectionId, formulaBlock.id, {
      latex,
      caption,
      equationNumber,
      displayMode,
    });
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit LaTeX Mathematical Formula"
      maxWidth="600px"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" icon={<Check size={16} />} onClick={handleSave}>
            Apply Formula
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {/* Live KaTeX Math Preview */}
        <div>
          <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Sigma size={14} style={{ color: 'var(--primary-600)' }} />
            <span>Mathematical Formula Preview</span>
          </label>
          <div
            style={{
              padding: '16px',
              backgroundColor: '#f8fafc',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-md)',
              minHeight: '64px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div ref={previewRef} />
          </div>
        </div>

        {/* Quick Symbol Shortcuts */}
        <div>
          <label className="form-label text-xs">Quick Symbols & Structures</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {COMMON_SYMBOLS.map((s) => (
              <button
                key={s.label}
                type="button"
                className="btn btn-ghost btn-xs"
                style={{ fontSize: '11px', border: '1px solid var(--border-subtle)', padding: '2px 6px' }}
                onClick={() => handleInsertSymbol(s.latex)}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* LaTeX Code Input */}
        <div>
          <label className="form-label">LaTeX Formula Code</label>
          <textarea
            className="form-input text-sm"
            rows={3}
            style={{ fontFamily: 'Courier New, monospace' }}
            value={latex}
            onChange={(e) => setLatex(e.target.value)}
            placeholder="e.g. V_{dc} = \frac{V_m}{\pi}"
          />
        </div>

        {/* Display Mode, Equation Number & Caption */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
          <div>
            <label className="form-label">Equation Number</label>
            <input
              type="text"
              className="form-input"
              value={equationNumber}
              onChange={(e) => setEquationNumber(e.target.value)}
              placeholder="e.g. (1) or (2.1)"
            />
          </div>

          <div>
            <label className="form-label">Formula Caption (Optional)</label>
            <input
              type="text"
              className="form-input"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="e.g. DC output voltage relationship"
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};
